import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const MAX_FILE_SIZE_BYTES = 20 * 1024 * 1024;
const DEFAULT_PHOTO_COUNT = 9;
const EXPORT_SIZE = 1200;
const EXPORT_DPI = 300;
const EXPORT_QUALITY = 0.92;
const DESKTOP_MQ = '(min-width: 750px)';
const ZOOM_STEP = 0.1;
const PAN_STEP_PX = 24;

/**
 * @typedef {{
 *   source: File,
 *   cropped: File | null,
 *   objectUrl: string,
 *   width: number,
 *   height: number,
 *   scale: number,
 *   offsetX: number,
 *   offsetY: number,
 * }} PhotoEntry
 */

class PhotoUploadPicker extends HTMLElement {
  /** @type {HTMLInputElement | null} */
  #input = null;
  /** @type {HTMLButtonElement | null} */
  #trigger = null;
  /** @type {HTMLElement | null} */
  #label = null;
  /** @type {HTMLElement | null} */
  #buttonText = null;
  /** @type {HTMLElement | null} */
  #preview = null;
  /** @type {HTMLElement | null} */
  #status = null;
  /** @type {HTMLDialogElement | null} */
  #cropDialog = null;
  /** @type {HTMLElement | null} */
  #cropTitle = null;
  /** @type {HTMLElement | null} */
  #cropFrame = null;
  /** @type {HTMLImageElement | null} */
  #cropImage = null;
  /** @type {HTMLInputElement | null} */
  #cropZoom = null;
  /** @type {HTMLButtonElement | null} */
  #cropZoomIn = null;
  /** @type {HTMLButtonElement | null} */
  #cropZoomOut = null;
  /** @type {HTMLButtonElement | null} */
  #cropConfirm = null;
  /** @type {HTMLButtonElement | null} */
  #cropBack = null;
  /** @type {HTMLButtonElement | null} */
  #cropClose = null;
  /** @type {HTMLFormElement | null} */
  #form = null;
  /** @type {HTMLElement | null} */
  #productForm = null;
  /** @type {MutationObserver | null} */
  #formObserver = null;
  /** @type {(PhotoEntry | null)[]} */
  #slots = [];
  /** @type {boolean} */
  #isAdding = false;
  /** @type {number} */
  #photoCount = DEFAULT_PHOTO_COUNT;
  /** @type {number} */
  #cropIndex = 0;
  /** @type {boolean} */
  #singleEdit = false;
  /** @type {number | null} */
  #pendingSlotIndex = null;
  /** @type {boolean} */
  #pickSingle = false;
  /** @type {number} */
  #coverScale = 1;
  /** @type {number} */
  #aspectW = 1;
  /** @type {number} */
  #aspectH = 1;
  /** @type {string} */
  #sizeLabel = '2×2 inch';
  /** @type {boolean} */
  #dragging = false;
  /** @type {number} */
  #pointerStartX = 0;
  /** @type {number} */
  #pointerStartY = 0;
  /** @type {number} */
  #offsetAtStartX = 0;
  /** @type {number} */
  #offsetAtStartY = 0;
  /** @type {number | null} */
  #pinchDistance = null;
  /** @type {number} */
  #pinchScale = 1;
  /** @type {string[]} */
  #previewObjectUrls = [];
  /** @type {MediaQueryList | null} */
  #desktopMq = null;

  /** @type {MutationObserver | null} */
  #domObserver = null;
  /** @type {MutationObserver | null} */
  #checkoutObserver = null;
  /** @type {number[]} */
  #rerenderTimers = [];
  /** @type {boolean} */
  #isRenderingPreview = false;
  /** @type {boolean} */
  #submitBound = false;
  /** @type {((event: Event) => void) | null} */
  #boundSubmitGuard = null;
  /** @type {((event: Event) => void) | null} */
  #boundBuyClickGuard = null;

  connectedCallback() {
    this.#photoCount = Number(this.dataset.defaultPhotoCount) || DEFAULT_PHOTO_COUNT;
    this.#aspectW = Math.max(0.1, Number(this.dataset.aspectW) || 1);
    this.#aspectH = Math.max(0.1, Number(this.dataset.aspectH) || 1);
    this.#sizeLabel = this.dataset.sizeLabel || '2×2 inch';
    this.#ensureSlots();
    this.#refreshDomRefs();

    if (!this.#input || !this.#trigger) return;

    this.#trigger.addEventListener('click', () => this.#openBatchPicker());
    this.#input.addEventListener('change', () => this.#onFilesChosen());
    document.addEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#desktopMq = window.matchMedia(DESKTOP_MQ);
    this.#desktopMq.addEventListener('change', this.#onViewportChange);
    this.#setupCropUi();
    this.#bindProductForm();
    this.#observeOwnDom();
    this.#refreshCountUi();
    this.#renderPreview();
    this.#closeCrop();
  }

  disconnectedCallback() {
    this.#formObserver?.disconnect();
    this.#domObserver?.disconnect();
    this.#checkoutObserver?.disconnect();
    this.#clearRerenderTimers();
    this.#unbindCheckoutGuards();
    document.removeEventListener(StandardEvents.productSelect, this.#onProductSelect);
    this.#desktopMq?.removeEventListener('change', this.#onViewportChange);
    this.#revokeAllUrls();
  }

  #refreshDomRefs() {
    this.#input = this.querySelector('[data-photo-picker-input]');
    this.#trigger = this.querySelector('[data-photo-picker-trigger]');
    this.#label = this.querySelector('[data-photo-label]');
    this.#buttonText = this.querySelector('[data-photo-button-text]');
    this.#preview = this.querySelector('[data-photo-preview]');
    this.#status = this.querySelector('[data-photo-status]');
    this.#cropDialog = this.querySelector('[data-photo-crop]');
    this.#cropTitle = this.querySelector('[data-crop-title]');
    this.#cropFrame = this.querySelector('[data-crop-frame]');
    this.#cropImage = this.querySelector('[data-crop-image]');
    this.#cropZoom = this.querySelector('[data-crop-zoom]');
    this.#cropZoomIn = this.querySelector('[data-crop-zoom-in]');
    this.#cropZoomOut = this.querySelector('[data-crop-zoom-out]');
    this.#cropConfirm = this.querySelector('[data-crop-confirm]');
    this.#cropBack = this.querySelector('[data-crop-back]');
    this.#cropClose = this.querySelector('[data-crop-close]');
  }

  #observeOwnDom() {
    this.#domObserver?.disconnect();
    this.#domObserver = new MutationObserver(() => {
      if (this.#isRenderingPreview) return;
      const preview = this.querySelector('[data-photo-preview]');
      // Theme morphs can replace the preview node and leave it empty.
      if (preview && preview !== this.#preview) {
        this.#refreshDomRefs();
        this.#renderPreview();
        return;
      }
      if (this.#isDesktop() && preview && preview.childElementCount === 0) {
        this.#refreshDomRefs();
        this.#renderPreview();
      }
    });
    this.#domObserver.observe(this, { childList: true, subtree: true });
  }

  #clearRerenderTimers() {
    this.#rerenderTimers.forEach((id) => window.clearTimeout(id));
    this.#rerenderTimers = [];
  }

  #schedulePreviewRefresh() {
    this.#clearRerenderTimers();
    const run = () => {
      this.#refreshDomRefs();
      this.#syncCountFromVariant();
      this.#ensureSlots();
      this.#refreshCountUi();
      this.#renderPreview();
      this.#attachForm();
      this.#enforceCheckoutLock();
    };
    run();
    // Run again after Shopify section/variant morphs settle (theme re-enables ATC).
    [0, 50, 150, 300, 600, 1000].forEach((delay) => {
      const id = window.setTimeout(run, delay);
      this.#rerenderTimers.push(id);
    });
  }

  #onViewportChange = () => {
    this.#refreshDomRefs();
    this.#refreshCountUi();
    this.#renderPreview();
  };

  #isDesktop() {
    return window.matchMedia(DESKTOP_MQ).matches;
  }

  #ensureSlots() {
    while (this.#slots.length < this.#photoCount) this.#slots.push(null);
    if (this.#slots.length > this.#photoCount) {
      this.#slots.splice(this.#photoCount).forEach((entry) => {
        if (entry) URL.revokeObjectURL(entry.objectUrl);
      });
    }
  }

  #onProductSelect = (event) => {
    const section = this.closest('.shopify-section');
    if (!(event.target instanceof Node) || !section?.contains(event.target)) return;
    this.#handleVariantChange();
  };

  #handleVariantChange() {
    this.#refreshDomRefs();
    const nextCount = this.#requiredCount();
    const countChanged = nextCount !== this.#photoCount;
    this.#syncCountFromVariant();

    if (countChanged) {
      this.#resetSelection();
      this.#enforceCheckoutLock();
    } else {
      this.#refreshCountUi();
      this.#renderPreview();
    }

    // Always re-draw after morphs so Set of 6/9 never loses the grid.
    this.#schedulePreviewRefresh();
  }

  #setupCropUi() {
    this.#cropConfirm?.addEventListener('click', () => this.#saveCurrentCrop());
    this.#cropBack?.addEventListener('click', () => {
      const prev = this.#previousFilledIndex(this.#cropIndex - 1);
      if (prev >= 0) this.#showCrop(prev);
    });
    this.#cropClose?.addEventListener('click', () => this.#dismissCrop());

    this.#cropZoom?.addEventListener('input', () => {
      const entry = this.#slots[this.#cropIndex];
      if (!entry || !this.#cropZoom) return;
      entry.scale = Number(this.#cropZoom.value) || 1;
      this.#clampEntry(entry);
      this.#paintCrop(entry);
    });

    // Prevent iOS Safari double-tap page zoom when tapping +/- buttons.
    this.#bindZoomButton(this.#cropZoomIn, ZOOM_STEP);
    this.#bindZoomButton(this.#cropZoomOut, -ZOOM_STEP);

    this.querySelectorAll('[data-crop-nudge]').forEach((button) => {
      if (!(button instanceof HTMLButtonElement)) return;
      const direction =
        button.getAttribute('data-crop-nudge') ||
        (button.classList.contains('photo-upload__crop-nudge--up')
          ? 'up'
          : button.classList.contains('photo-upload__crop-nudge--down')
            ? 'down'
            : button.classList.contains('photo-upload__crop-nudge--left')
              ? 'left'
              : button.classList.contains('photo-upload__crop-nudge--right')
                ? 'right'
                : '');
      this.#bindNudgeButton(button, direction);
    });

    this.#cropFrame?.addEventListener('pointerdown', (event) => {
      if (event.button === 2) return;
      const entry = this.#slots[this.#cropIndex];
      if (!entry) return;
      this.#dragging = true;
      this.#pointerStartX = event.clientX;
      this.#pointerStartY = event.clientY;
      this.#offsetAtStartX = entry.offsetX;
      this.#offsetAtStartY = entry.offsetY;
      this.#cropFrame?.setPointerCapture?.(event.pointerId);
    });

    window.addEventListener('pointermove', (event) => {
      if (!this.#dragging) return;
      const entry = this.#slots[this.#cropIndex];
      if (!entry) return;
      entry.offsetX = this.#offsetAtStartX + (event.clientX - this.#pointerStartX);
      entry.offsetY = this.#offsetAtStartY + (event.clientY - this.#pointerStartY);
      this.#clampEntry(entry);
      this.#paintCrop(entry);
    });

    const stopDrag = () => {
      this.#dragging = false;
    };
    window.addEventListener('pointerup', stopDrag);
    window.addEventListener('pointercancel', stopDrag);

    this.#cropFrame?.addEventListener(
      'touchstart',
      (event) => {
        if (event.touches.length === 2) {
          event.preventDefault();
          this.#pinchDistance = this.#touchDistance(event.touches);
          this.#pinchScale = this.#slots[this.#cropIndex]?.scale || 1;
        }
      },
      { passive: false }
    );

    this.#cropFrame?.addEventListener(
      'touchmove',
      (event) => {
        if (event.touches.length !== 2 || this.#pinchDistance == null) return;
        event.preventDefault();
        const entry = this.#slots[this.#cropIndex];
        if (!entry) return;
        const next = Math.min(3, Math.max(1, this.#pinchScale * (this.#touchDistance(event.touches) / this.#pinchDistance)));
        entry.scale = next;
        if (this.#cropZoom) this.#cropZoom.value = String(next);
        this.#clampEntry(entry);
        this.#paintCrop(entry);
      },
      { passive: false }
    );

    this.#cropFrame?.addEventListener('touchend', () => {
      this.#pinchDistance = null;
    });

    this.#cropDialog?.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.#dismissCrop();
    });
  }

  /** @param {TouchList} touches */
  #touchDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
  }

  #bindProductForm() {
    if (this.#attachForm()) {
      this.#observeCheckoutControls();
      return;
    }
    this.#formObserver = new MutationObserver(() => {
      if (this.#attachForm()) {
        this.#formObserver?.disconnect();
        this.#observeCheckoutControls();
      }
    });
    this.#formObserver.observe(document.body, { childList: true, subtree: true });
  }

  #attachForm() {
    const sectionId = this.dataset.sectionId;
    let form = null;
    if (sectionId) {
      form = document.getElementById(`BuyButtons-ProductForm-${sectionId}`);
    }
    if (!form) {
      form = this.closest('.shopify-section')?.querySelector('product-form-component form') ?? null;
    }
    if (!form) return false;

    const productForm = form.closest('product-form-component');
    const formChanged = form !== this.#form || productForm !== this.#productForm;

    if (formChanged) {
      this.#unbindCheckoutGuards();
    }

    this.#form = form;
    this.#productForm = productForm;
    this.#form.setAttribute('enctype', 'multipart/form-data');
    this.#syncCountFromVariant();

    if (formChanged || !this.#submitBound) {
      this.#bindCheckoutGuards();
    }

    if (formChanged) {
      const onVariantChange = () => this.#handleVariantChange();
      this.#form.querySelector('[name="id"]')?.addEventListener('change', onVariantChange);
      const section = this.closest('.shopify-section');
      const picker = section?.querySelector('variant-picker');
      picker?.addEventListener('change', onVariantChange);
      picker?.querySelectorAll('input[type="radio"]').forEach((input) => {
        input.addEventListener('change', onVariantChange);
      });
      picker?.addEventListener('click', () => {
        window.setTimeout(() => {
          const nextCount = this.#requiredCount();
          if (nextCount !== this.#photoCount) {
            this.#handleVariantChange();
            return;
          }
          this.#schedulePreviewRefresh();
        }, 0);
      });
    }

    this.#enforceCheckoutLock();
    return true;
  }

  #unbindCheckoutGuards() {
    if (this.#boundSubmitGuard) {
      this.#form?.removeEventListener('submit', this.#boundSubmitGuard, true);
      this.#productForm?.removeEventListener('submit', this.#boundSubmitGuard, true);
    }
    if (this.#boundBuyClickGuard) {
      const section = this.closest('.shopify-section');
      section?.removeEventListener('click', this.#boundBuyClickGuard, true);
      section?.removeEventListener('pointerdown', this.#boundBuyClickGuard, true);
    }
    this.#boundSubmitGuard = null;
    this.#boundBuyClickGuard = null;
    this.#submitBound = false;
  }

  #bindCheckoutGuards() {
    this.#boundSubmitGuard = (event) => {
      if (!this.#allReady()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.#setStatus(`Please add and adjust all ${this.#photoCount} photos.`, true);
        this.#enforceCheckoutLock();
        return;
      }
      if (!this.#ensureConsentSelected()) {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        this.#setStatus('Please choose a Content & Social Media option.', true);
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#addToCart();
    };

    this.#boundBuyClickGuard = (event) => {
      if (this.#allReady()) return;
      const target = event.target;
      if (!(target instanceof Element)) return;
      const buyControl = target.closest(
        '[ref="addToCartButton"], add-to-cart-component, .accelerated-checkout-block, .mm-paypal-checkout, shopify-accelerated-checkout, .shopify-payment-button, [data-shopify="payment-button"]'
      );
      if (!buyControl) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.#setStatus(`Please add and adjust all ${this.#photoCount} photos.`, true);
      this.#enforceCheckoutLock();
    };

    this.#form?.addEventListener('submit', this.#boundSubmitGuard, true);
    this.#productForm?.addEventListener('submit', this.#boundSubmitGuard, true);
    const section = this.closest('.shopify-section');
    section?.addEventListener('click', this.#boundBuyClickGuard, true);
    section?.addEventListener('pointerdown', this.#boundBuyClickGuard, true);

    const consentRoot = this.#consentRoot();
    consentRoot?.querySelectorAll('[data-consent-input]').forEach((input) => {
      input.addEventListener('change', () => {
        consentRoot.classList.remove('content-consent--error');
        const hint = consentRoot.querySelector('[data-consent-hint]');
        if (hint instanceof HTMLElement) hint.hidden = true;
      });
    });

    this.#submitBound = true;
  }

  #observeCheckoutControls() {
    const section = this.closest('.shopify-section');
    if (!section || this.#checkoutObserver) return;

    this.#checkoutObserver = new MutationObserver(() => {
      if (this.#allReady()) return;
      const button = this.#productForm?.querySelector('[ref="addToCartButton"]');
      if (button instanceof HTMLButtonElement && !button.disabled) {
        this.#enforceCheckoutLock();
        return;
      }
      const unlockedExpress = section.querySelector(
        '.accelerated-checkout-block:not(.mm-photos-locked), .mm-paypal-checkout:not(.mm-photos-locked)'
      );
      if (unlockedExpress) this.#enforceCheckoutLock();
    });

    this.#checkoutObserver.observe(section, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['disabled', 'hidden', 'class'],
    });
  }

  #enforceCheckoutLock() {
    const locked = !this.#allReady();
    this.#setAddToCartDisabled(locked);
    this.#setExpressCheckoutLocked(locked);
  }

  /** @param {boolean} locked */
  #setExpressCheckoutLocked(locked) {
    const root =
      this.#productForm?.closest('.product-details, .product-information, .shopify-section') ??
      this.closest('.shopify-section');
    if (!root) return;

    root
      .querySelectorAll('.accelerated-checkout-block, .mm-paypal-checkout, [data-shopify="payment-button"]')
      .forEach((el) => {
        if (!(el instanceof HTMLElement)) return;
        el.classList.toggle('mm-photos-locked', locked);
        if (locked) el.setAttribute('aria-disabled', 'true');
        else el.removeAttribute('aria-disabled');
      });
  }

  #countFromLabel(label) {
    const text = (label || '').trim();
    if (!text) return null;
    const match = text.match(/(\d{1,3})/);
    return match ? Number(match[1]) : null;
  }

  #requiredCount() {
    const section = this.closest('.shopify-section');
    const picker = section?.querySelector('variant-picker');

    const checked = picker?.querySelector('input[type="radio"]:checked');
    if (checked) {
      const checkedLabel = checked.closest('label')?.textContent?.trim() ?? checked.value ?? '';
      const fromChecked = this.#countFromLabel(checkedLabel);
      if (fromChecked) return fromChecked;
    }

    // Fallback: selected/aria-pressed pill text.
    const selectedPill =
      picker?.querySelector('[aria-checked="true"], [aria-pressed="true"], .is-selected, [data-selected="true"]') ??
      null;
    const fromPill = this.#countFromLabel(selectedPill?.textContent?.trim() ?? '');
    if (fromPill) return fromPill;

    const select = picker?.querySelector('select');
    const fromSelect = this.#countFromLabel(select?.selectedOptions?.[0]?.textContent?.trim() ?? '');
    if (fromSelect) return fromSelect;

    return Number(this.dataset.defaultPhotoCount) || DEFAULT_PHOTO_COUNT;
  }

  #syncCountFromVariant() {
    this.#photoCount = this.#requiredCount();
    this.dataset.defaultPhotoCount = String(this.#photoCount);
    this.#ensureSlots();
  }

  #revokeAllUrls() {
    this.#slots.forEach((entry) => {
      if (entry) URL.revokeObjectURL(entry.objectUrl);
    });
    this.#previewObjectUrls.forEach((url) => URL.revokeObjectURL(url));
    this.#previewObjectUrls = [];
  }

  #resetSelection() {
    this.#revokeAllUrls();
    this.#slots = [];
    this.#ensureSlots();
    this.#cropIndex = 0;
    this.#singleEdit = false;
    this.#pendingSlotIndex = null;
    this.#pickSingle = false;
    if (this.#input) {
      this.#input.multiple = true;
      this.#input.value = '';
    }
    this.#closeCrop();
    this.#renderPreview();
  }

  #filledCount() {
    return this.#slots.filter(Boolean).length;
  }

  #readyCount() {
    return this.#slots.filter((entry) => entry?.cropped).length;
  }

  /** @param {number} index */
  #clearSlot(index) {
    const entry = this.#slots[index];
    if (!entry) return;

    URL.revokeObjectURL(entry.objectUrl);
    this.#slots[index] = null;

    if (this.#cropDialog?.open && this.#cropIndex === index) {
      this.#closeCrop();
      this.#singleEdit = false;
    }

    if (this.#input) this.#input.value = '';

    this.#renderPreview();
    this.#refreshCountUi();

    const ready = this.#readyCount();
    const filled = this.#filledCount();
    if (filled === 0) {
      this.#setStatus(
        this.#isDesktop()
          ? `Click any square to add a photo, or use Choose to pick one, a few, or all ${this.#photoCount}.`
          : `Add photos one by one or a few at a time — then fit each into the ${this.#sizeLabel} frame.`,
        false
      );
      this.#enforceCheckoutLock();
      return;
    }

    if (this.#allReady()) {
      this.#setStatus(`${this.#photoCount} magnet photos ready. Click a photo to re-adjust.`, false);
      this.#enforceCheckoutLock();
      return;
    }

    this.#setStatus(`${ready} of ${this.#photoCount} ready. Click an empty square or Add more photos.`, false);
    this.#enforceCheckoutLock();
  }

  #allReady() {
    return this.#slots.length === this.#photoCount && this.#slots.every((entry) => Boolean(entry?.cropped));
  }

  #openBatchPicker() {
    if (!this.#input) return;
    this.#pendingSlotIndex = null;
    this.#pickSingle = false;
    this.#input.multiple = true;
    this.#input.click();
  }

  /** @param {number} index */
  #openSlotPicker(index) {
    if (!this.#input) return;
    this.#pendingSlotIndex = index;
    this.#pickSingle = true;
    this.#input.multiple = false;
    this.#input.click();
  }

  #refreshCountUi() {
    const count = this.#photoCount;
    const ready = this.#readyCount();

    if (this.#label) {
      this.#label.textContent = `Upload Your ${count} Photos`;
    }

    if (this.#buttonText) {
      if (ready === 0) {
        this.#buttonText.textContent = `Choose My ${count} Photos`;
      } else if (ready < count) {
        this.#buttonText.textContent = `Add more photos (${ready}/${count})`;
      } else {
        this.#buttonText.textContent = `Change photos`;
      }
    }

    if (this.#status && this.#filledCount() === 0) {
      this.#setStatus(
        this.#isDesktop()
          ? `Click any square to add a photo, or use Choose to pick one, a few, or all ${count}.`
          : `Add photos one by one or a few at a time — then fit each into the ${this.#sizeLabel} frame.`,
        false
      );
    }

    if (this.#preview) {
      const columns = count <= 9 ? 3 : 4;
      this.#preview.dataset.columns = String(columns);
      this.#preview.style.gridTemplateColumns = `repeat(${columns}, 1fr)`;
      // Soft 2.75×3.5 slots stay narrower; don't force full-width square grid.
      if (this.#aspectW !== this.#aspectH) {
        this.#preview.style.maxWidth = '18rem';
        this.#preview.style.alignItems = 'start';
      } else {
        this.#preview.style.maxWidth = '';
        this.#preview.style.alignItems = '';
      }
    }
  }

  async #onFilesChosen() {
    if (!this.#input) return;

    if (!this.#form && !this.#attachForm()) {
      this.#setStatus('Product form is not ready yet. Please try again.', true);
      return;
    }

    this.#syncCountFromVariant();
    const chosen = Array.from(this.#input.files ?? []).filter((file) => file.type.startsWith('image/'));
    const slotStart = this.#pendingSlotIndex;
    const single = this.#pickSingle;
    this.#pendingSlotIndex = null;
    this.#pickSingle = false;
    this.#input.multiple = true;
    this.#input.value = '';

    if (!chosen.length) return;

    if (chosen.some((file) => file.size > MAX_FILE_SIZE_BYTES)) {
      this.#setStatus('Each photo must be smaller than 20 MB.', true);
      return;
    }

    if (slotStart == null && this.#allReady()) {
      this.#revokeAllUrls();
      this.#slots = [];
      this.#ensureSlots();
    }

    this.#setAddToCartDisabled(true);
    this.#setStatus('Preparing your photos…', false);

    try {
      /** @type {number[]} */
      const touched = [];

      if (slotStart != null) {
        const built = await this.#buildEntry(chosen[0]);
        const previous = this.#slots[slotStart];
        if (previous) URL.revokeObjectURL(previous.objectUrl);
        this.#slots[slotStart] = built;
        touched.push(slotStart);
      } else {
        let emptyIndexes = this.#slots.map((entry, index) => (entry ? -1 : index)).filter((index) => index >= 0);
        if (!emptyIndexes.length) {
          this.#revokeAllUrls();
          this.#slots = [];
          this.#ensureSlots();
          emptyIndexes = this.#slots.map((_, index) => index);
        }

        const accepted = chosen.slice(0, emptyIndexes.length);
        if (chosen.length > emptyIndexes.length) {
          this.#setStatus(
            `Only ${emptyIndexes.length} open square${emptyIndexes.length === 1 ? '' : 's'} — added the first ${emptyIndexes.length}.`,
            false
          );
        }

        const built = await Promise.all(accepted.map((file) => this.#buildEntry(file)));
        built.forEach((entry, offset) => {
          const index = emptyIndexes[offset];
          this.#slots[index] = entry;
          touched.push(index);
        });
      }

      this.#singleEdit = Boolean(single && touched.length === 1);
      this.#refreshCountUi();
      this.#renderPreview();
      this.#showCrop(touched[0]);
    } catch (error) {
      console.error(error);
      this.#setStatus('Could not read one of the photos. Please try again.', true);
      this.#renderPreview();
      this.#refreshCountUi();
    }
  }

  /** @param {File} file */
  #buildEntry(file) {
    return new Promise((resolve, reject) => {
      const objectUrl = URL.createObjectURL(file);
      const image = new Image();
      image.onload = () => {
        resolve({
          source: file,
          cropped: null,
          objectUrl,
          width: image.naturalWidth,
          height: image.naturalHeight,
          scale: 1,
          offsetX: 0,
          offsetY: 0,
        });
      };
      image.onerror = () => {
        URL.revokeObjectURL(objectUrl);
        reject(new Error(`Failed to load ${file.name}`));
      };
      image.src = objectUrl;
    });
  }

  /** @param {number} index */
  #showCrop(index) {
    const entry = this.#slots[index];
    if (!this.#cropDialog || !this.#cropImage || !entry) return;

    this.#cropIndex = index;
    const hasLaterUncropped = this.#nextUncroppedIndex(index + 1) >= 0;

    if (this.#cropTitle) this.#cropTitle.textContent = `Adjust photo ${index + 1} of ${this.#photoCount}`;
    if (this.#cropBack) this.#cropBack.hidden = this.#singleEdit || this.#previousFilledIndex(index - 1) < 0;
    if (this.#cropConfirm) this.#cropConfirm.textContent = this.#singleEdit || !hasLaterUncropped ? 'Save' : 'Next';

    this.#cropImage.onload = () => {
      this.#measureCover(entry);
      if (this.#cropZoom) this.#cropZoom.value = String(entry.scale);
      this.#paintCrop(entry);
    };
    this.#cropImage.src = entry.objectUrl;

    if (!this.#cropDialog.open) {
      this.#cropDialog.showModal();
      document.documentElement.classList.add('photo-upload-crop-open');
    }
  }

  /** @param {number} from */
  #nextUncroppedIndex(from) {
    for (let i = from; i < this.#slots.length; i += 1) {
      const entry = this.#slots[i];
      if (entry && !entry.cropped) return i;
    }
    return -1;
  }

  /** @param {number} from */
  #previousFilledIndex(from) {
    for (let i = from; i >= 0; i -= 1) {
      if (this.#slots[i]) return i;
    }
    return -1;
  }

  #closeCrop() {
    this.#cropDialog?.close();
    document.documentElement.classList.remove('photo-upload-crop-open');
  }

  /** Close adjuster without saving the current crop step. */
  #dismissCrop() {
    if (!this.#cropDialog?.open) {
      document.documentElement.classList.remove('photo-upload-crop-open');
      return;
    }
    this.#closeCrop();
    this.#singleEdit = false;
    this.#renderPreview();
    this.#refreshCountUi();

    const ready = this.#readyCount();
    if (this.#allReady()) {
      this.#setStatus(`${this.#photoCount} magnet photos ready. Click a photo to re-adjust.`, false);
    } else if (ready > 0) {
      this.#setStatus(`Saved ${ready} of ${this.#photoCount}. Add the rest when you’re ready.`, false);
    } else {
      this.#setStatus(`Add and adjust all ${this.#photoCount} photos before checkout.`, false);
    }
    this.#enforceCheckoutLock();
  }

  /** @returns {{ width: number, height: number }} */
  #frameSize() {
    if (!this.#cropFrame) return { width: 280, height: 280 };
    const width = this.#cropFrame.clientWidth || 280;
    const height = this.#cropFrame.clientHeight || Math.round(width * (this.#aspectH / this.#aspectW));
    return { width, height };
  }

  /** @returns {{ width: number, height: number }} */
  #exportDimensions() {
    if (this.#aspectW !== this.#aspectH) {
      return {
        width: Math.round(this.#aspectW * EXPORT_DPI),
        height: Math.round(this.#aspectH * EXPORT_DPI),
      };
    }
    return { width: EXPORT_SIZE, height: EXPORT_SIZE };
  }

  /** @param {number} delta */
  #nudgeZoom(delta) {
    const entry = this.#slots[this.#cropIndex];
    if (!entry) return;
    const next = Math.min(3, Math.max(1, (entry.scale || 1) + delta));
    entry.scale = next;
    if (this.#cropZoom) this.#cropZoom.value = String(next);
    this.#clampEntry(entry);
    this.#paintCrop(entry);
  }

  /**
   * Pan step in px — same feel as dragging the photo with a finger.
   * @returns {number}
   */
  #panStep() {
    const { width: frameW, height: frameH } = this.#frameSize();
    return Math.max(PAN_STEP_PX, Math.round(Math.min(frameW, frameH) * 0.06));
  }

  /**
   * Arrow direction = photo moves that way on screen (identical to finger drag).
   * ↑ photo up · ↓ photo down · ← photo left · → photo right
   * @param {string} direction
   */
  #nudgePan(direction) {
    const entry = this.#slots[this.#cropIndex];
    if (!entry) return;

    // No inversion: ↑ photo up, ↓ down, ← left, → right (on screen).
    const amount = this.#panStep();
    const dx = direction === 'right' ? -amount : direction === 'left' ? amount : 0;
    const dy = direction === 'down' ? -amount : direction === 'up' ? amount : 0;
    if (!dx && !dy) return;

    entry.offsetX += dx;
    entry.offsetY += dy;
    this.#clampEntry(entry);
    this.#paintCrop(entry);
  }

  /**
   * Bind zoom +/- so taps adjust the crop image only — never browser page zoom.
   * @param {HTMLButtonElement | null} button
   * @param {number} delta
   */
  #bindZoomButton(button, delta) {
    if (!button) return;

    /** @type {boolean} */
    let handledByTouch = false;

    button.addEventListener(
      'touchend',
      (event) => {
        // Block Safari double-tap page zoom; apply crop zoom once.
        event.preventDefault();
        event.stopPropagation();
        handledByTouch = true;
        this.#nudgeZoom(delta);
      },
      { passive: false }
    );

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (handledByTouch) {
        handledByTouch = false;
        return;
      }
      this.#nudgeZoom(delta);
    });
  }

  /**
   * Bind pan arrows so taps nudge the photo in that arrow's direction.
   * Same touch/click pattern as zoom +/- for reliable iOS taps; hold to repeat.
   * @param {HTMLButtonElement} button
   * @param {string} direction
   */
  #bindNudgeButton(button, direction) {
    if (!direction) return;

    /** @type {number | null} */
    let repeatTimer = null;
    /** @type {number | null} */
    let holdTimer = null;
    /** @type {boolean} */
    let handledByTouch = false;

    const clearRepeat = () => {
      if (holdTimer != null) {
        window.clearTimeout(holdTimer);
        holdTimer = null;
      }
      if (repeatTimer != null) {
        window.clearInterval(repeatTimer);
        repeatTimer = null;
      }
    };

    const startRepeat = () => {
      clearRepeat();
      this.#nudgePan(direction);
      holdTimer = window.setTimeout(() => {
        repeatTimer = window.setInterval(() => this.#nudgePan(direction), 50);
      }, 280);
    };

    button.addEventListener(
      'touchstart',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        handledByTouch = true;
        startRepeat();
      },
      { passive: false }
    );

    button.addEventListener(
      'touchend',
      (event) => {
        event.preventDefault();
        event.stopPropagation();
        clearRepeat();
      },
      { passive: false }
    );

    button.addEventListener('touchcancel', () => clearRepeat());

    button.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'touch' || event.button === 2) return;
      event.preventDefault();
      event.stopPropagation();
      startRepeat();
    });

    button.addEventListener('pointerup', (event) => {
      if (event.pointerType === 'touch') return;
      clearRepeat();
    });
    button.addEventListener('pointercancel', () => clearRepeat());
    button.addEventListener('pointerleave', (event) => {
      if (event.pointerType === 'touch') return;
      clearRepeat();
    });

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (handledByTouch) {
        handledByTouch = false;
        return;
      }
      // Keyboard / accessibility activation
      this.#nudgePan(direction);
    });
  }

  /** @param {PhotoEntry} entry */
  #measureCover(entry) {
    if (!this.#cropFrame) return;
    const { width: frameW, height: frameH } = this.#frameSize();
    this.#coverScale = Math.max(frameW / entry.width, frameH / entry.height);
    // Start slightly zoomed so ↑↓←→ always have room to shift the photo.
    if (!entry.scale || entry.scale < 1.2) entry.scale = 1.2;
    this.#clampEntry(entry);
  }

  /** @param {PhotoEntry} entry */
  #clampEntry(entry) {
    if (!this.#cropFrame) return;
    const { width: frameW, height: frameH } = this.#frameSize();
    const drawnScale = this.#coverScale * entry.scale;
    const drawnW = entry.width * drawnScale;
    const drawnH = entry.height * drawnScale;
    const maxX = Math.max(0, (drawnW - frameW) / 2);
    const maxY = Math.max(0, (drawnH - frameH) / 2);
    entry.offsetX = Math.min(maxX, Math.max(-maxX, entry.offsetX));
    entry.offsetY = Math.min(maxY, Math.max(-maxY, entry.offsetY));
  }

  /** @param {PhotoEntry} entry */
  #paintCrop(entry) {
    if (!this.#cropImage || !this.#cropFrame) return;
    const { width: frameW, height: frameH } = this.#frameSize();
    const drawnScale = this.#coverScale * entry.scale;
    const drawnW = entry.width * drawnScale;
    const drawnH = entry.height * drawnScale;
    this.#cropImage.style.width = `${drawnW}px`;
    this.#cropImage.style.height = `${drawnH}px`;
    this.#cropImage.style.left = `${(frameW - drawnW) / 2 + entry.offsetX}px`;
    this.#cropImage.style.top = `${(frameH - drawnH) / 2 + entry.offsetY}px`;
  }

  async #saveCurrentCrop() {
    const entry = this.#slots[this.#cropIndex];
    if (!entry) return;

    try {
      if (this.#cropConfirm) this.#cropConfirm.disabled = true;
      entry.cropped = await this.#exportEntry(entry);
      this.#renderPreview();
      this.#refreshCountUi();

      if (this.#singleEdit) {
        this.#finishCropFlow();
        return;
      }

      const next = this.#nextUncroppedIndex(this.#cropIndex + 1);
      if (next >= 0) {
        this.#showCrop(next);
        return;
      }

      this.#finishCropFlow();
    } catch (error) {
      console.error(error);
      this.#setStatus('Could not crop this photo. Please try again.', true);
    } finally {
      if (this.#cropConfirm) this.#cropConfirm.disabled = false;
    }
  }

  #finishCropFlow() {
    this.#closeCrop();
    this.#singleEdit = false;
    this.#renderPreview();
    this.#refreshCountUi();

    const ready = this.#readyCount();
    if (this.#allReady()) {
      this.#setStatus(`${this.#photoCount} magnet photos ready. Click a photo to re-adjust.`, false);
      this.#enforceCheckoutLock();
      return;
    }

    this.#setStatus(`${ready} of ${this.#photoCount} ready. Click an empty square or Add more photos.`, false);
    this.#enforceCheckoutLock();
  }

  /** @param {PhotoEntry} entry */
  async #exportEntry(entry) {
    if (!this.#cropFrame) throw new Error('Missing crop frame');

    const { width: frameW, height: frameH } = this.#frameSize();
    const drawnScale = this.#coverScale * entry.scale;
    const drawnW = entry.width * drawnScale;
    const drawnH = entry.height * drawnScale;
    const left = (frameW - drawnW) / 2 + entry.offsetX;
    const top = (frameH - drawnH) / 2 + entry.offsetY;
    const sourceX = -left / drawnScale;
    const sourceY = -top / drawnScale;
    const sourceW = frameW / drawnScale;
    const sourceH = frameH / drawnScale;
    const exportSize = this.#exportDimensions();

    const canvas = document.createElement('canvas');
    canvas.width = exportSize.width;
    canvas.height = exportSize.height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Canvas unsupported');

    const image = await this.#loadImage(entry.objectUrl);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, exportSize.width, exportSize.height);
    ctx.drawImage(image, sourceX, sourceY, sourceW, sourceH, 0, 0, exportSize.width, exportSize.height);

    const blob = await new Promise((resolve, reject) => {
      canvas.toBlob((result) => (result ? resolve(result) : reject(new Error('Export failed'))), 'image/jpeg', EXPORT_QUALITY);
    });

    const baseName = entry.source.name.replace(/\.[^.]+$/, '') || 'photo';
    return new File([blob], `${baseName}-magnet.jpg`, { type: 'image/jpeg' });
  }

  /** @param {string} url */
  #loadImage(url) {
    return new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(image);
      image.onerror = reject;
      image.src = url;
    });
  }

  async #addToCart() {
    if (!this.#form || !this.#productForm || !this.#allReady() || this.#isAdding) return;

    if (!this.#ensureConsentSelected()) {
      this.#setStatus('Please choose a Content & Social Media option.', true);
      return;
    }

    this.#isAdding = true;
    this.#setAddToCartDisabled(true);
    this.#setStatus('Uploading photos and adding to cart…', false);

    const variantId = this.#form.querySelector('[name="id"]')?.value;
    const quantityInput = this.#form.querySelector('[name="quantity"]');
    const quantity = quantityInput instanceof HTMLInputElement ? quantityInput.value : '1';
    const itemCount = Number(quantity) || 1;

    if (!variantId) {
      this.#isAdding = false;
      this.#setAddToCartDisabled(false);
      this.#setStatus('Could not find product variant. Please refresh the page.', true);
      return;
    }

    const formData = new FormData();
    formData.set('id', variantId);
    formData.set('quantity', quantity);
    this.#appendConsentProperty(formData);
    this.#appendExtraProperties(formData);
    formData.set('properties[Magnet size]', this.#sizeLabel);

    this.#slots.forEach((entry, index) => {
      if (!entry?.cropped) return;
      formData.append(`properties[Photo ${index + 1}]`, entry.cropped, entry.cropped.name);
    });

    const sectionIds = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.push(item.dataset.sectionId);
    });
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }

    const deferredEventPromise = CartLinesUpdateEvent.createPromise();
    this.#productForm.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity: itemCount }],
        promise: deferredEventPromise.promise,
      })
    );

    try {
      const response = await fetch(window.Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
          'X-Requested-With': 'XMLHttpRequest',
        },
        body: formData,
      });
      const data = await response.json();
      if (data.status) throw new Error(data.message || data.description || 'Add to cart failed');

      /** @type {import('@theme/component-cart-items').CartItemsComponent | null} */
      const cartItemsComponent = document.querySelector('cart-items-component');
      let ajaxCart;
      if (cartItemsComponent?.fetchCartData) {
        await customElements.whenDefined('cart-items-component');
        ajaxCart = await cartItemsComponent.fetchCartData();
      } else {
        const cartResponse = await fetch(`${window.Theme.routes.cart_url}.json`, {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        });
        if (!cartResponse.ok) throw new Error('Failed to refresh cart');
        ajaxCart = await cartResponse.json();
      }

      deferredEventPromise.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source: 'photo-upload-picker',
          sourceId: this.#productForm.id,
          itemCount,
          productId: this.#productForm.dataset.productId,
          sections: data.sections,
          didError: false,
        },
      });
      this.#setStatus('Added to cart!', false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add to cart';
      deferredEventPromise.reject(error);
      this.#productForm.dispatchEvent(new CartErrorEvent({ error: message, code: 'INVALID' }));
      this.#setStatus(message, true);
      this.#setAddToCartDisabled(false);
    } finally {
      this.#isAdding = false;
    }
  }

  #consentRoot() {
    const section = this.closest('.shopify-section');
    return (
      section?.querySelector('[data-content-consent]') ??
      document.querySelector('[data-content-consent]')
    );
  }

  /** @returns {HTMLInputElement | null} */
  #selectedConsentInput() {
    const root = this.#consentRoot();
    const checked = root?.querySelector('[data-consent-input]:checked, input[type="radio"][name^="properties["]:checked');
    return checked instanceof HTMLInputElement ? checked : null;
  }

  /** @returns {boolean} */
  #ensureConsentSelected() {
    const root = this.#consentRoot();
    if (!root) return true;

    const selected = this.#selectedConsentInput();
    const hint = root.querySelector('[data-consent-hint]');
    if (selected) {
      root.classList.remove('content-consent--error');
      if (hint instanceof HTMLElement) hint.hidden = true;
      return true;
    }

    root.classList.add('content-consent--error');
    if (hint instanceof HTMLElement) hint.hidden = false;
    root.scrollIntoView({ behavior: 'smooth', block: 'center' });
    return false;
  }

  /** @param {FormData} formData */
  #appendConsentProperty(formData) {
    const root = this.#consentRoot();
    const selected = this.#selectedConsentInput();
    if (!selected) return;

    const propertyName =
      (root instanceof HTMLElement && root.dataset.consentProperty?.trim()) || 'Social media';
    const key = `properties[${propertyName}]`;

    // Prefer a single explicit consent property at the top of the line item.
    if (!formData.has(key)) formData.append(key, selected.value);
  }

  /** @param {FormData} formData */
  #appendExtraProperties(formData) {
    if (!this.#form) return;
    const formId = this.#form.id;
    const fields = new Set([
      ...this.#form.querySelectorAll('[name^="properties["]'),
      ...document.querySelectorAll(`[form="${formId}"][name^="properties["]`),
    ]);

    fields.forEach((field) => {
      if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) return;
      if (field instanceof HTMLInputElement && field.type === 'file') return;
      if (field instanceof HTMLInputElement && (field.type === 'checkbox' || field.type === 'radio')) {
        if (!field.checked) return;
        // Consent is appended first via #appendConsentProperty — skip duplicates.
        if (field.hasAttribute('data-consent-input') || /social media/i.test(field.name)) return;
        formData.append(field.name, field.value);
        return;
      }
      if (field.value) formData.append(field.name, field.value);
    });
  }

  #renderPreview() {
    this.#isRenderingPreview = true;
    try {
      this.#refreshDomRefs();
      if (!this.#preview) return;

      this.#previewObjectUrls.forEach((url) => URL.revokeObjectURL(url));
      this.#previewObjectUrls = [];
      this.#preview.innerHTML = '';
      this.#ensureSlots();

      // Desktop: always show all squares. Mobile: only after the first photo.
      if (!this.#isDesktop() && this.#filledCount() === 0) return;

      for (let index = 0; index < this.#photoCount; index += 1) {
        const entry = this.#slots[index];
        const button = document.createElement('button');
        button.type = 'button';
        const softAspect = this.#aspectW !== this.#aspectH;
        const applySoftAspect = (el) => {
          if (!softAspect) return;
          el.style.aspectRatio = `${this.#aspectW} / ${this.#aspectH}`;
        };

        if (!entry) {
          button.className = 'photo-upload__preview-item photo-upload__preview-item--empty';
          button.setAttribute('aria-label', `Add photo ${index + 1}`);
          button.innerHTML = `<span>${index + 1}</span><em>+</em>`;
          applySoftAspect(button);
          button.addEventListener('click', () => this.#openSlotPicker(index));
          this.#preview.appendChild(button);
          continue;
        }

        const item = document.createElement('div');
        item.className = 'photo-upload__preview-item';
        applySoftAspect(item);
        if (!entry.cropped) item.classList.add('photo-upload__preview-item--pending');

        button.className = 'photo-upload__preview-thumb';
        button.setAttribute('aria-label', `Adjust photo ${index + 1}`);

        const img = document.createElement('img');
        img.alt = `Photo ${index + 1}`;
        if (entry.cropped) {
          const url = URL.createObjectURL(entry.cropped);
          this.#previewObjectUrls.push(url);
          img.src = url;
        } else {
          img.src = entry.objectUrl;
        }

        const badge = document.createElement('span');
        badge.textContent = `${index + 1}`;
        button.append(img, badge);
        button.addEventListener('click', () => {
          this.#singleEdit = true;
          this.#showCrop(index);
        });

        const remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'photo-upload__preview-remove';
        remove.setAttribute('aria-label', `Remove photo ${index + 1}`);
        remove.innerHTML = '<span aria-hidden="true">×</span>';
        remove.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          this.#clearSlot(index);
        });

        item.append(button, remove);
        this.#preview.appendChild(item);
      }
    } finally {
      this.#isRenderingPreview = false;
    }
  }

  /** @param {string} message @param {boolean} isError */
  #setStatus(message, isError) {
    if (!this.#status) return;
    this.#status.textContent = message;
    this.#status.classList.toggle('photo-upload__status--error', isError);
  }

  /** @param {boolean} disabled */
  #setAddToCartDisabled(disabled) {
    const section = this.#productForm?.closest('.shopify-section') ?? this.closest('.shopify-section');
    const buttons = [
      ...(this.#productForm?.querySelectorAll('[ref="addToCartButton"]') ?? []),
      ...(section?.querySelectorAll('sticky-add-to-cart [ref="addToCartButton"]') ?? []),
      ...(section?.querySelectorAll('add-to-cart-component button[type="submit"], button[name="add"]') ?? []),
    ];
    const unique = [...new Set(buttons)];
    unique.forEach((button) => {
      if (button instanceof HTMLButtonElement && !button.hasAttribute('data-sold-out')) {
        button.disabled = disabled;
        button.setAttribute('aria-disabled', disabled ? 'true' : 'false');
        if (disabled) button.setAttribute('data-mm-photos-lock', '1');
        else button.removeAttribute('data-mm-photos-lock');
      }
    });

    this.#productForm?.querySelectorAll('add-to-cart-component').forEach((container) => {
      if (!(container instanceof HTMLElement)) return;
      if (disabled && typeof container.disable === 'function') container.disable();
      if (!disabled && typeof container.enable === 'function' && this.#allReady()) container.enable();
    });
  }
}

if (!customElements.get('photo-upload-picker')) {
  customElements.define('photo-upload-picker', PhotoUploadPicker);
}
